/**
 * useSupplierForm Hook
 * Centraliserad state management och logik för leverantörsformulär
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useCrudOperations } from '../../../components/Hooks/useCrudOperations';
import { API_URL, getAvatarUrl } from '../../../utils/api';
import { invalidateGlobalCache } from '../../../hooks/useApiCache';
import { 
  prepareSupplierDataForCreate, 
  prepareSupplierDataForUpdate, 
  convertToFormData,
  validateAvatarFile
} from '../domain/supplierTransforms';

/**
 * Custom hook för leverantörsformulär
 */
export const useSupplierForm = ({
  initialSupplier = null,
  users = [],
  onClose,
  onSuccess,
  setSuppliers,
  suppliers,
  isUpdate = false
}) => {
  const getInitialValues = useCallback(() => {
    if (isUpdate && initialSupplier) {
      return {
        supplierType: initialSupplier.supplierType || '',
        companyName: initialSupplier.companyName || '',
        website: initialSupplier.website || '',
        // Enkel kontaktperson utan användarkonto. Modellen och backend har
        // haft fälten hela tiden — formuläret exponerade dem inte, så enda
        // vägen till en kontakt var att skapa en användare med rollen
        // materialleverantör.
        contactPerson: initialSupplier.contactPerson || '',
        email: initialSupplier.email || '',
        phone: initialSupplier.phone || '',
        discountRate: initialSupplier.discountRate || '',
        discountDescription: initialSupplier.discountDescription || '',
        avatar: initialSupplier.avatar || '',
        removeAvatar: false,
        contactMethod: initialSupplier.contactMethod || 'direct',
        affiliateUrl: initialSupplier.affiliateUrl || '',
        primaryContactPerson: initialSupplier.primaryContactPerson?._id || '',
        showInBrief: initialSupplier.showInBrief !== false,
        showInOffers: initialSupplier.showInOffers !== false
      };
    }
    
    return {
      supplierType: '',
      companyName: '',
      website: '',
      contactPerson: '',
      email: '',
      phone: '',
      discountRate: '',
      discountDescription: '',
      contactMethod: 'direct',
      affiliateUrl: '',
      primaryContactPerson: '',
      showInBrief: true,
      showInOffers: true
    };
  }, [initialSupplier, isUpdate]);

  const [values, setValues] = useState(getInitialValues());
  const [discounts, setDiscounts] = useState(initialSupplier?.discounts || []);
  const [avatar, setAvatar] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(
    isUpdate && initialSupplier?.avatar ? getAvatarUrl(initialSupplier.avatar) : ''
  );
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { addItemWithFile, updateItem } = useCrudOperations(
    isUpdate ? `${API_URL}/suppliers` : `${API_URL}/suppliers/add`, 
    setSuppliers, 
    suppliers
  );

  useEffect(() => {
    if (isUpdate && initialSupplier) {
      setValues(getInitialValues());
      setDiscounts(initialSupplier.discounts || []);
      setPreviewUrl(initialSupplier.avatar ? getAvatarUrl(initialSupplier.avatar) : '');
    }
  }, [initialSupplier, isUpdate, getInitialValues]);

  // Filtrerade användare kopplade till denna leverantör
  const supplierUsers = useMemo(() => {
    if (!isUpdate || !initialSupplier) return [];
    
    return users.filter(user => 
      user.role === 'materialleverantör' && 
      (user.supplier === initialSupplier._id || user.supplier?._id === initialSupplier._id)
    );
  }, [users, initialSupplier, isUpdate]);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setValues(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }, []);

  const handleAvatarChange = useCallback((file) => {
    const validation = validateAvatarFile(file);
    
    if (!validation.isValid) {
      setErrorMessage(validation.error);
      return;
    }
    
    setAvatar(file);
    setPreviewUrl(URL.createObjectURL(file));
    setValues(prev => ({ ...prev, removeAvatar: false }));
    setErrorMessage('');
  }, []);

  const handleRemoveAvatar = useCallback(() => {
    setPreviewUrl('');
    setAvatar(null);
    setValues(prev => ({ ...prev, avatar: '', removeAvatar: true }));
  }, []);

  const addDiscount = useCallback(() => {
    setDiscounts(prev => [...prev, { category: '', rate: '' }]);
  }, []);

  const updateDiscount = useCallback((index, field, value) => {
    setDiscounts(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  }, []);

  const removeDiscount = useCallback((index) => {
    setDiscounts(prev => prev.filter((_, i) => i !== index));
  }, []);

  const invalidateCaches = useCallback(() => {
    invalidateGlobalCache([
      'dashboard_suppliers',
      'myassigned_suppliers',
      'myprojects_suppliers',
      'myprojects_favorites'
    ]);
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    try {
      let supplierData;
      
      if (isUpdate) {
        supplierData = prepareSupplierDataForUpdate(values, discounts);
        
        let body;
        let isFormData = false;
        
        if (values.removeAvatar || avatar) {
          body = convertToFormData(supplierData, avatar);
          isFormData = true;
        } else {
          body = {
            ...supplierData,
            avatar: values.avatar,
            removeAvatar: false,
          };
        }
        
        await updateItem(initialSupplier._id, body, isFormData);
      } else {
        supplierData = prepareSupplierDataForCreate(values, discounts);
        const formData = convertToFormData(supplierData, avatar);
        await addItemWithFile(formData);
      }
      
      invalidateCaches();
      onClose();
      
      if (onSuccess) {
        onSuccess();
      }
      
    } catch (error) {
      console.error('Form submission error:', error);
      setErrorMessage(isUpdate ? 'Kunde inte uppdatera leverantören' : 'Kunde inte lägga till leverantören');
    } finally {
      setIsLoading(false);
    }
  }, [values, discounts, avatar, isUpdate, initialSupplier, addItemWithFile, updateItem, invalidateCaches, onClose, onSuccess]);

  return {
    values,
    setValues,
    discounts,
    avatar,
    previewUrl,
    isLoading,
    errorMessage,
    supplierUsers,
    handleChange,
    handleAvatarChange,
    handleRemoveAvatar,
    addDiscount,
    updateDiscount,
    removeDiscount,
    handleSubmit
  };
};

